import { logger } from '@enterprise/common';

const PERSISTENT_ITEM_KEY_PREFIX = '#';
const storage = window.localStorage;

/**
 * Wrapper over local storage that supports persistent items that can only be explicitly removed.
 */
export interface LocalStorage extends Storage {
  keys(): string[];
  removePersistentItem(key: string): void;
  setPersistentItem(key: string, value: string): void;
  getPersistentItem(key: string): string | null;
}

export const localStorage: LocalStorage = {
  get length() {
    return this.keys().length;
  },
  clear(): void {
    this.keys().forEach((key) => storage.removeItem(key));
  },
  keys(): string[] {
    return Object.keys(storage).filter((key) => !isPersistentItemKey(key));
  },
  key(index: number): string | null {
    return this.keys()[index] ?? null;
  },
  removeItem(key: string): void {
    if (!isPersistentItemKey(key)) {
      storage.removeItem(key);
    } else {
      logger.warn(`To remove persistent item use "removePersistentItem"`);
    }
  },
  setItem(key: string, value: string): void {
    if (!isPersistentItemKey(key)) {
      storage.setItem(key, value);
    } else {
      logger.warn(`To set persistent item use "setPersistentItem"`);
    }
  },
  getItem(key: string): string | null {
    if (!isPersistentItemKey(key)) {
      return storage.getItem(key);
    } else {
      logger.warn(`To get persistent item use "getPersistentItem"`);
      return null;
    }
  },
  removePersistentItem(key: string) {
    storage.removeItem(withPrefix(key));
  },
  setPersistentItem(key: string, value: string) {
    storage.setItem(withPrefix(key), value);
  },
  getPersistentItem(key: string): string | null {
    return storage.getItem(withPrefix(key));
  }
};

function isPersistentItemKey(key: string): boolean {
  return key.startsWith(PERSISTENT_ITEM_KEY_PREFIX);
}

function withPrefix(key: string): string {
  return isPersistentItemKey(key) ? key : `${PERSISTENT_ITEM_KEY_PREFIX}${key}`;
}
